import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "bg-white border border-gray-200 rounded-lg p-6" }
const _hoisted_2 = { class: "text-lg font-semibold text-black mb-4" }
const _hoisted_3 = {
  key: 0,
  class: "mt-4"
}
const _hoisted_4 = { key: 1 }
const _hoisted_5 = {
  key: 0,
  class: "mt-4"
}
const _hoisted_6 = { class: "w-full border border-gray-300 rounded-md table-fixed" }
const _hoisted_7 = { class: "px-4 py-2" }
const _hoisted_8 = { class: "text-sm font-medium text-black" }
const _hoisted_9 = { class: "px-4 py-2" }
const _hoisted_10 = ["value", "onInput"]
const _hoisted_11 = { class: "px-4 py-2" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "mt-4" }
const _hoisted_14 = {
  key: 0,
  class: "mb-2 text-sm text-red-600"
}
const _hoisted_15 = ["disabled"]
const _hoisted_16 = { key: 0 }
const _hoisted_17 = { key: 1 }
const _hoisted_18 = {
  key: 1,
  class: "mt-2 text-sm text-red-600"
}
const _hoisted_19 = {
  key: 2,
  class: "mt-4"
}
const _hoisted_20 = {
  key: 0,
  class: "mb-4 p-4 bg-red-50 border border-red-200 rounded-md"
}
const _hoisted_21 = { class: "overflow-x-auto" }
const _hoisted_22 = { class: "min-w-full border border-gray-300 rounded-md table-auto" }
const _hoisted_23 = { class: "bg-gray-50" }
const _hoisted_24 = {
  key: 0,
  class: "px-4 py-2 text-left text-sm font-medium text-black border-b",
  style: {"width":"120px"}
}
const _hoisted_25 = { class: "px-4 py-2 text-sm" }
const _hoisted_26 = { class: "px-4 py-2" }
const _hoisted_27 = ["href"]
const _hoisted_28 = {
  key: 1,
  class: "text-sm"
}
const _hoisted_29 = { class: "px-4 py-2" }
const _hoisted_30 = ["href"]
const _hoisted_31 = {
  key: 1,
  class: "text-black text-sm cursor-not-allowed",
  title: "Veuillez uploader une facture pour télécharger l'étiquette"
}
const _hoisted_32 = {
  key: 0,
  class: "px-4 py-2"
}
const _hoisted_33 = {
  key: 3,
  class: "mt-4 text-center py-4"
}

import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  inject,
  type ComputedRef,
} from "vue";
import {
  getOrderLabels,
  getUploadedInvoices,
  getShippingDocument,
  generateLabelEasy,
} from "@/api/orders";
import type {
  OrderLabel,
  LabelPropsWithLivraison,
  OrderDetailResponse,
} from "@/types";
import { getTrackingUrl } from "@/utils/trackingUrl";
import { useNotification } from "@/composables/useNotification";
import { isGpxCarrier } from "@/utils/labelModuleSelector";
import {
  getTotalPoidsNonRupture,
  distributePoidsColis,
  getColisPoidsValidationMessage,
} from "@/utils/parcelWeights";
import AdminLabelDeleteButton from "@/components/order/labels/AdminLabelDeleteButton.vue";
import DownloadAllLabelsButton from "@/components/order/labels/DownloadAllLabelsButton.vue";
import { useAuthStore } from "@/stores/auth";


export default /*@__PURE__*/_defineComponent({
  __name: 'LabelEasy',
  props: {
    carrierName: {},
    idLivraison: {},
    nbColisMax: {},
    poidsTotal: {},
    canGenerateLabels: { type: Boolean, default: true },
    dimensionErrors: { default: () => [] },
    parcelWeights: {},
    parcelDimensions: {},
    useParcelDimensions: { type: Boolean },
    orderId: {},
    orderStatus: {},
    generationEntry: { default: "form" }
  },
  emits: ["labels-generated", "request-generate-labels"],
  setup(__props: any, { emit: __emit }) {

const authStore = useAuthStore();
const isAdmin = computed(() => authStore.isImpersonating);


const props = __props;

const emit = __emit;

// Injecter les données de la commande depuis la modal
const orderDetailDataFromInject = inject<
  ComputedRef<{ value: OrderDetailResponse | null }>
>(
  "orderDetailData",
  computed(() => ({ value: null })),
);
const orderData = computed(() => orderDetailDataFromInject.value?.value);
const isGpx = computed(() => isGpxCarrier(props.idLivraison));

// Déterminer si c'est un groupage
const isGroupage = computed(() => {
  if (!orderData.value) return false;
  const groupageValue = orderData.value.commandeGroupe?.groupage;
  const isSingleGpxOrder =
    isGpx.value && Number(orderData.value.groupageReadiness?.countTotal) === 1;
  return (
    !isSingleGpxOrder &&
    (groupageValue === 1 ||
      String(groupageValue) === "1" ||
      orderData.value.groupage === true)
  );
});
// Déterminer l'ID correct pour rechercher les étiquettes
const searchOrderId = computed(() => {
  if (!props.orderId) return null;
  return props.orderId;
});

// Watcher pour recharger les étiquettes quand orderDetailData change
watch(
  () => orderDetailDataFromInject.value.value,
  (newOrderDetailData) => {
    if (newOrderDetailData) {
      loadLabels();
    }
  },
  { immediate: false }, // Ne pas charger immédiatement, attendre que les données soient disponibles
);

const hasUploadedInvoices = ref(false);
const canDownloadLabels = computed(() => hasUploadedInvoices.value);
// Nombre de colis fixe (basé sur la commande, non modifiable)
const colisPoids = ref<number[]>([]);
const isGenerating = ref(false);

const initColisFromProps = () => {
  const totalPoids = getTotalPoidsNonRupture(orderData.value?.lignes);
  const n = Math.max(
    props.nbColisMax || 1,
    Math.max(1, Math.ceil(totalPoids / 25)),
  );
  colisPoids.value = distributePoidsColis(totalPoids, n);
};

watch(
  orderData,
  (newOrderData) => {
    if (newOrderData) {
      initColisFromProps();
    }
  },
  { immediate: true },
);

const formValidationMessage = computed(() => {
  const poidsMessage = getColisPoidsValidationMessage(effectiveColisPoids.value);
  if (poidsMessage) return poidsMessage;
  return "Veuillez remplir tous les champs obligatoires";
});

const effectiveColisPoids = computed(() =>
  props.useParcelDimensions ? props.parcelWeights || [] : colisPoids.value,
);

// Validation du formulaire
const isFormValid = computed(() => {
  // Vérifier que tous les poids des colis sont remplis (> 0)
  const allPoidsFilled = effectiveColisPoids.value.every(
    (poids) => poids > 0 && poids <= 25,
  );
  if (!allPoidsFilled) return false;

  return true;
});

const addColis = () => {
  colisPoids.value.push(0);
};

const removeColis = (index: number) => {
  if (colisPoids.value.length <= 1) return;
  colisPoids.value.splice(index, 1);
};

const { success, error: showError } = useNotification();

const generateLabels = async () => {
  // Empêcher les clics multiples
  if (isGenerating.value) {
    return;
  }
  isGenerating.value = true;
  try {
    const response = await generateLabelEasy({
      id_commande: props.orderId,
      nbColis: effectiveColisPoids.value.length,
      colisPoids: effectiveColisPoids.value,
      parcelDimensions: props.parcelDimensions,
    });

    if (response.success && response.etiquettes) {
      success("Étiquettes générées avec succès");
      labels.value = response.etiquettes;
      emit("labels-generated");
    } else {
      showError(
        response.error || "Erreur lors de la génération des étiquettes",
      );
    }
  } catch (err: unknown) {
    const errorMessage =
      err instanceof Error
        ? err.message
        : "Erreur lors de la génération des étiquettes";
    showError(errorMessage);
  } finally {
    isGenerating.value = false;
  }
};

// Vérifier les factures existantes
const checkInvoices = async () => {
  if (!props.orderId) return;
  const [invoicesResult, shippingDocumentResult] = await Promise.allSettled([
    getUploadedInvoices(props.orderId),
    getShippingDocument(props.orderId),
  ]);
  hasUploadedInvoices.value =
    (invoicesResult.status === "fulfilled" &&
      invoicesResult.value.length > 0) ||
    (shippingDocumentResult.status === "fulfilled" &&
      shippingDocumentResult.value.exists);
};

const labels = ref<OrderLabel[]>([]);
const isLoadingLabels = ref(false);

const hasLabels = computed(() => labels.value.length > 0);

const numericOrderStatus = computed(() => Number(props.orderStatus));

const canRequestOrGenerateLabels = computed(() => {
  const status = numericOrderStatus.value;
  // Pas en NOUVEAU (1/9) ni ANNULÉE (3)
  return ![1, 3, 9].includes(status) && status > 0 && !hasLabels.value;
});

const showGenerateForm = computed(
  () => props.generationEntry === "form" && canRequestOrGenerateLabels.value,
);

const showGenerateEntryButton = computed(
  () => props.generationEntry === "button" && canRequestOrGenerateLabels.value,
);

const handleLabelDeleted = () => {
  loadLabels();
};

// Charger les étiquettes depuis l'API
const loadLabels = async () => {
  if (!searchOrderId.value) return;

  isLoadingLabels.value = true;
  try {
    const etiquettes = await getOrderLabels(searchOrderId.value);
    labels.value = etiquettes;
  } catch (error) {
    labels.value = [];
  } finally {
    isLoadingLabels.value = false;
  }
};

// Utiliser la fonction utilitaire pour générer l'URL de tracking
const getTrackingUrlForLabel = (
  trackingNumber: string,
  idLivraison: number,
): string | null => {
  const langue = "fr";
  return getTrackingUrl(trackingNumber, idLivraison, langue);
};

onMounted(() => {
  checkInvoices();
  // Ne charger les étiquettes que si orderDetailData est déjà disponible
  if (orderDetailDataFromInject.value.value) {
    loadLabels();
  }
  window.addEventListener("invoiceUploaded", checkInvoices);
  window.addEventListener("invoiceDeleted", checkInvoices);
});

onUnmounted(() => {
  window.removeEventListener("invoiceUploaded", checkInvoices);
  window.removeEventListener("invoiceDeleted", checkInvoices);
});

const blockNonNumeric = (event: KeyboardEvent) => {
  const allowed = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ];
  if (allowed.includes(event.key)) return;
  if (
    (event.ctrlKey || event.metaKey) &&
    ["a", "c", "v", "x"].includes(event.key)
  )
    return;
  if (event.key === "." || event.key === ",") return;
  if (event.key < "0" || event.key > "9") event.preventDefault();
};

const handlePaste = (event: ClipboardEvent) => {
  const paste = event.clipboardData?.getData("text") || "";
  if (!/^\d*\.?\d*$/.test(paste)) event.preventDefault();
};

const handlePoidsChange = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement | null;
  if (!target) return;
  const value = target.value ? Number(target.value) : 0;
  const next = [...colisPoids.value];
  next[index] = value;
  colisPoids.value = next;
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h4", _hoisted_2, " Télécharger vos étiquettes " + _toDisplayString(_ctx.carrierName), 1),
    (showGenerateEntryButton.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _cache[4] || (_cache[4] = _createElementVNode("p", { class: "mb-3 text-sm text-gray-700" }, " Renseignez la facture, les dimensions et le poids des colis pour générer vos étiquettes. ", -1)),
          _createElementVNode("button", {
            type: "button",
            class: "px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('request-generate-labels')))
          }, _cache[3] || (_cache[3] = [
            _createElementVNode("i", { class: "fas fa-tags mr-2" }, null, -1),
            _createTextVNode(" Générer vos étiquettes ")
          ]))
        ]))
      : (showGenerateForm.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("form", {
              onSubmit: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"])),
              class: "space-y-4"
            }, [
              (!props.useParcelDimensions)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createElementVNode("table", _hoisted_6, [
                      _cache[6] || (_cache[6] = _createElementVNode("thead", { class: "bg-gray-50" }, [
                        _createElementVNode("tr", null, [
                          _createElementVNode("th", { class: "px-4 py-2 text-left text-sm font-medium text-black border-b w-1/3" }, " Colis "),
                          _createElementVNode("th", { class: "px-4 py-2 text-left text-sm font-medium text-black border-b w-1/2" }, " Poids (kg) "),
                          _createElementVNode("th", { class: "px-4 py-2 border-b w-auto" })
                        ])
                      ], -1)),
                      _createElementVNode("tbody", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(colisPoids.value, (poids, index) => {
                          return (_openBlock(), _createElementBlock("tr", {
                            key: index,
                            class: "border-b"
                          }, [
                            _createElementVNode("td", _hoisted_7, [
                              _createElementVNode("label", _hoisted_8, "Colis n°" + _toDisplayString(index + 1), 1)
                            ]),
                            _createElementVNode("td", _hoisted_9, [
                              _createElementVNode("input", {
                                value: colisPoids.value[index],
                                type: "number",
                                step: "0.001",
                                min: "0",
                                max: "25",
                                class: "numbersonly w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500",
                                onWheel: _cache[1] || (_cache[1] = (e) => (e.target as HTMLInputElement).blur()),
                                onKeydown: blockNonNumeric,
                                onPaste: handlePaste,
                                onInput: (e) => handlePoidsChange(index, e)
                              }, null, 40, _hoisted_10)
                            ]),
                            _createElementVNode("td", _hoisted_11, [
                              (colisPoids.value.length > 1)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 0,
                                    type: "button",
                                    onClick: ($event: any) => (removeColis(index)),
                                    class: "px-2 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors",
                                    title: "Supprimer ce colis"
                                  }, _cache[5] || (_cache[5] = [
                                    _createElementVNode("i", { class: "fas fa-trash-alt" }, null, -1)
                                  ]), 8, _hoisted_12))
                                : _createCommentVNode("", true)
                            ])
                          ]))
                        }), 128))
                      ])
                    ]),
                    _createElementVNode("button", {
                      type: "button",
                      onClick: addColis,
                      class: "mt-3 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    }, _cache[7] || (_cache[7] = [
                      _createElementVNode("i", { class: "fas fa-plus mr-2" }, null, -1),
                      _createTextVNode("Ajouter un colis ")
                    ])),
                    _cache[8] || (_cache[8] = _createElementVNode("p", { class: "mt-2 text-xs text-red-600" }, " Le poids d'un colis ne doit pas dépasser 25 kg. ", -1))
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_13, [
                (props.dimensionErrors && props.dimensionErrors.length > 0)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_14, [
                      _cache[9] || (_cache[9] = _createElementVNode("i", { class: "fas fa-exclamation-circle mr-1" }, null, -1)),
                      _createTextVNode(" " + _toDisplayString(props.dimensionErrors.join(" | ")), 1)
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  type: "button",
                  disabled: isGenerating.value || !isFormValid.value || !_ctx.canGenerateLabels,
                  onClick: _withModifiers(generateLabels, ["prevent","stop"]),
                  class: "px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                }, [
                  (isGenerating.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_16, _cache[10] || (_cache[10] = [
                        _createElementVNode("i", { class: "fas fa-spinner fa-spin mr-2" }, null, -1),
                        _createTextVNode(" Génération en cours... ")
                      ])))
                    : (_openBlock(), _createElementBlock("span", _hoisted_17, " Générer vos étiquettes "))
                ], 8, _hoisted_15),
                (!isFormValid.value && formValidationMessage.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_18, [
                      _cache[11] || (_cache[11] = _createElementVNode("i", { class: "fas fa-exclamation-circle mr-1" }, null, -1)),
                      _createTextVNode(" " + _toDisplayString(formValidationMessage.value), 1)
                    ]))
                  : _createCommentVNode("", true)
              ])
            ], 32)
          ]))
        : _createCommentVNode("", true),
    (
        hasLabels.value &&
        (numericOrderStatus.value === 4 ||
          numericOrderStatus.value === 2 ||
          (isGroupage.value &&
            (numericOrderStatus.value === 7 || numericOrderStatus.value === 8)))
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
          (!canDownloadLabels.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_20, _cache[12] || (_cache[12] = [
                _createElementVNode("p", { class: "text-sm text-red-800" }, [
                  _createElementVNode("i", { class: "fas fa-exclamation-triangle mr-2" }),
                  _createElementVNode("strong", null, "Attention :"),
                  _createTextVNode(" Vous devez uploader une facture avant de pouvoir télécharger les étiquettes. ")
                ], -1)
              ])))
            : _createCommentVNode("", true),
          (canDownloadLabels.value)
            ? (_openBlock(), _createBlock(DownloadAllLabelsButton, {
                key: 1,
                "order-id": _ctx.orderId,
                "label-count": labels.value.length
              }, null, 8, ["order-id", "label-count"]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_21, [
            _createElementVNode("table", _hoisted_22, [
              _createElementVNode("thead", _hoisted_23, [
                _createElementVNode("tr", null, [
                  _cache[13] || (_cache[13] = _createElementVNode("th", {
                    class: "px-4 py-2 text-left text-sm font-medium text-black border-b",
                    style: {"width":"130px"}
                  }, " Numéro de colis ", -1)),
                  _cache[14] || (_cache[14] = _createElementVNode("th", {
                    class: "px-4 py-2 text-left text-sm font-medium text-black border-b",
                    style: {"width":"130px"}
                  }, " Numéro de suivi ", -1)),
                  _cache[15] || (_cache[15] = _createElementVNode("th", {
                    class: "px-4 py-2 text-left text-sm font-medium text-black border-b",
                    style: {"width":"200px"}
                  }, " étiquettes ", -1)),
                  (isAdmin.value)
                    ? (_openBlock(), _createElementBlock("th", _hoisted_24, " Admin "))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(labels.value, (etiquette, index) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: etiquette.id_commande_etiquette,
                    class: "border-b hover:bg-gray-50"
                  }, [
                    _createElementVNode("td", _hoisted_25, "Colis " + _toDisplayString(index + 1), 1),
                    _createElementVNode("td", _hoisted_26, [
                      (
                    getTrackingUrlForLabel(
                      etiquette.tracking_number,
                      etiquette.id_livraison,
                    )
                  )
                        ? (_openBlock(), _createElementBlock("a", {
                            key: 0,
                            href: getTrackingUrlForLabel(etiquette.tracking_number, etiquette.id_livraison)!,
                            target: "_blank",
                            class: "text-indigo-600 hover:text-indigo-800 underline text-sm font-medium"
                          }, [
                            _createElementVNode("strong", null, _toDisplayString(etiquette.tracking_number), 1)
                          ], 8, _hoisted_27))
                        : (_openBlock(), _createElementBlock("strong", _hoisted_28, _toDisplayString(etiquette.tracking_number), 1))
                    ]),
                    _createElementVNode("td", _hoisted_29, [
                      (canDownloadLabels.value)
                        ? (_openBlock(), _createElementBlock("a", {
                            key: 0,
                            href: etiquette.url,
                            target: "_blank",
                            class: "text-pink-600 hover:text-pink-800 underline text-sm"
                          }, " Téléchargez l'étiquette colis ", 8, _hoisted_30))
                        : (_openBlock(), _createElementBlock("span", _hoisted_31, " Téléchargez l'étiquette colis "))
                    ]),
                    (isAdmin.value)
                      ? (_openBlock(), _createElementBlock("td", _hoisted_32, [
                          (_ctx.orderId)
                            ? (_openBlock(), _createBlock(AdminLabelDeleteButton, {
                                key: 0,
                                "order-id": _ctx.orderId,
                                "label-id": etiquette.id_commande_etiquette,
                                "tracking-number": etiquette.tracking_number,
                                onDeleted: handleLabelDeleted
                              }, null, 8, ["order-id", "label-id", "tracking-number"]))
                            : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true)
                  ]))
                }), 128))
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    (isLoadingLabels.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_33, _cache[16] || (_cache[16] = [
          _createElementVNode("i", { class: "fas fa-spinner fa-spin text-black" }, null, -1),
          _createElementVNode("span", { class: "ml-2 text-sm text-black" }, "Chargement des étiquettes...", -1)
        ])))
      : _createCommentVNode("", true)
  ]))
}
}

})